import {effect, Injectable, signal, WritableSignal, inject} from '@angular/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import {UiStateService} from '@llm/service/signals_ui_state.service';
import {llmStoreService, RecordingStatus} from '@llm/service/llm_store.service'
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";
import {MicrophoneService,} from '@serv/media_access/api_web_access_microphone.service';
import * as UtilityMediaAccess from '@serv/media_access/utility';
import {AppVisibilityService} from '@serv/media_access/app_visibility.service';

@Injectable({
  providedIn: 'root'
})
export class AudioRecordingService {
  private readonly CONSUMER_ID: UtilityMediaAccess.MicrophoneConsumer = 'RECORDING_USER_ENTRY';
  private store = inject(llmStoreService);
  private _uiStateService = inject(UiStateService);
  private _console = inject(Tools_aiConsoleService);
  private _accessMicSvc = inject(MicrophoneService)
  private _appVisbilitySvc = inject(AppVisibilityService)

  public isMicrophoneReady: WritableSignal<boolean> = signal(false);

  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private stateSubject = new BehaviorSubject<RecordingStatus>(RecordingStatus.INACTIVE);
  private audioBlobSubject = new Subject<Blob>();
  private errorSubject = new Subject<string>();
  private silenceDetectionEnabled = false;
  private silenceTimeout: any;
  private silenceThreshold = 5; // Limite de volume para silêncio
  private silenceDuration = 3000; // 5 segundos de silêncio para parar

  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array | null = null;
  private bufferLength: number = 0; // Usaremos analyser.fftSize para getByteTimeDomainData
  private amplitudeSubject = new BehaviorSubject<number>(0);
  public amplitude$: Observable<number> = this.amplitudeSubject.asObservable();


  audioBlob$: Observable<Blob> = this.audioBlobSubject.asObservable();
  error$: Observable<string> = this.errorSubject.asObservable();

  constructor() {
    effect(() => {
      if (this.store.recordingStatus() === RecordingStatus.RECORDING) {
        this.store.setRecording(true);
      } else {
        this.store.setRecording(false);
      }
    }, { allowSignalWrites: true });
    effect(() => {
      const isVisible = this._appVisbilitySvc.visibility();
      if (!isVisible && this.store.isRecording()) {
        this._console.log('[AudioRecordingService] Página oculta. Parando a gravação para liberar recursos.');
        this.stopRecording();
      }
    });

    this.stateSubject.subscribe(state => {
      this.store.setRecordingStatus(state);
    });
  }

  private getSupportedMimeType(): string {
    const mimeTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];
    for (const mimeType of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        this._console.log(`MimeType suportado: ${mimeType}`);
        return mimeType;
      }
    }
    this._console.log('Nenhum MimeType específico suportado, usando o padrão do navegador.');
    return ''; // Fallback to browser default
  }

  /**
   * Solicita permissão de acesso ao microfone e inicializa o MediaRecorder e AudioContext.
   * Só cria um novo MediaStream e MediaRecorder se não houver um ativo.
   * Este método deve ser chamado antes de tentar iniciar a gravação.
   */
  async requestPermission(): Promise<void> {
    try {
      if (!this.stream || !this.stream.active) {
        this.stream = await this._accessMicSvc.getOrCreateMicrophoneStream()
        if (this.stream) {
          const mimeType = this.getSupportedMimeType();
          const options = mimeType ? { mimeType } : {};
          this.mediaRecorder = new MediaRecorder(this.stream, options);
          this.setupMediaRecorder();
          this.initializeAudioContext();
          this.isMicrophoneReady.set(true);
          this._console.log('Permissão do microfone concedida e gravador inicializado.');
        } else {
          this.errorSubject.next('Não foi possível acessar o microfone. Garanta as permissões necessárias.')
          this._console.error('Erro ao acessar o microfone: o stream não foi obtido.');
          this.isMicrophoneReady.set(false);
          this.cleanupAudioContext();
        }
      } else {
        this.isMicrophoneReady.set(true);
        this._console.log('Permissão do microfone já concedida.');
      }
    } catch (err) {
      this.errorSubject.next('Não foi possível acessar o microfone. Garanta as permissões necessárias.');
      this._console.error('Erro ao acessar o microfone:', err);
      this.isMicrophoneReady.set(false);
      this.mediaRecorder = null;
      this.stream = null;
      this.cleanupAudioContext();
    }
  }

  /**
   * Inicializa o AudioContext e o AnalyserNode.
   * Só inicializa se não houver um contexto de áudio já existente.
   */
  private initializeAudioContext(): void {
    if (this.stream && !this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = this.audioContext.createMediaStreamSource(this.stream);

      this.analyser = this.audioContext.createAnalyser();

      // Configuración optimizada para detección de amplitud
      this.analyser.fftSize = 1024;  // Tamaño óptimo para análisis de amplitud
      this.analyser.smoothingTimeConstant = 0.8; // Suavizado moderado

      this.bufferLength = this.analyser.fftSize;
      this.dataArray = new Uint8Array(this.bufferLength);

      source.connect(this.analyser);
      // No conectar al destino para evitar eco
    }
  }

  /**
   * Configura os manipuladores de eventos para o MediaRecorder.
   */
  private setupMediaRecorder(): void {
    if (!this.mediaRecorder) return;

    this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) {
        this.audioChunks.push(event.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      this._handleMediaRecorderStop();
    };

    this.mediaRecorder.onstart = () => {
      this.store.setRecordingStatus(RecordingStatus.RECORDING);
      if (this.silenceDetectionEnabled) {
        this.startSilenceDetection();
      }
      this.startAmplitudeAnalysis(); // Iniciar a análise de áudio
      this._console.log('Gravação iniciada.');
    };

    this.mediaRecorder.onpause = () => {
      this.store.setRecordingStatus(RecordingStatus.PAUSED);
      this.stopSilenceDetection();
      this.stopAmplitudeAnalysis(); // Detener el análisis de audio
      this._console.log('Gravação pausada.');
    };

    this.mediaRecorder.onresume = () => {
      this.store.setRecordingStatus(RecordingStatus.RECORDING);
      if (this.silenceDetectionEnabled) {
        this.startSilenceDetection();
      }
      this.startAmplitudeAnalysis(); // Reanudar el análisis de audio
      this._console.log('Gravação retomada.');
    };
  }

  /**
   * Inicia a gravação de áudio.
   * Primeiro, tenta garantir as permissões do microfone e inicializar o gravador.
   */
  async startRecording(): Promise<void> {
      try {
          // 1. Assegura que temos um gravador válido e pronto para uso.
          // Esta chamada irá confirmar a configuração existente ou criar uma nova.
          await this.requestPermission();

          // 2. Após a permissão/configuração assíncrona, realiza uma verificação rigorosa.
          if (!this.mediaRecorder || !this.stream?.active) {
              this._console.error('Gravação falhou: MediaRecorder ou stream de áudio não está ativo.');
              this.errorSubject.next('Não foi possível iniciar a gravação. O microfone pode não estar disponível.');
              return; // Aborta se não estiver pronto
          }

          // 3. Se o gravador estiver pronto e inativo, inicia-o.
          if (this.mediaRecorder.state === RecordingStatus.INACTIVE) {
              this._console.log('Iniciando gravação...');
              this.audioChunks = [];
              this.mediaRecorder.start();
              this._uiStateService.startRecording();
          } else {
              this._console.warn(`Tentativa de iniciar gravação, mas o estado atual é: ${this.mediaRecorder.state}.`);
          }
      } catch (error) {
          // Este único bloco catch lida com erros de requestPermission() E mediaRecorder.start()
          this._console.error('Falha geral ao iniciar a gravação:', error);
          this.errorSubject.next('Não foi possível iniciar a gravação. Verifique as permissões do microfone.');

          // Limpa o estado para garantir que o próximo clique comece do zero.
          this.stopDestroy();
    }
  }

  /**
   * Para a gravação de áudio.
   */
  stopRecording(): void {
    this._console.log('Parando gravação...');
    if (this.mediaRecorder &&
      (this.mediaRecorder.state === RecordingStatus.RECORDING || this.mediaRecorder.state === RecordingStatus.PAUSED)) {
        this.mediaRecorder.stop();
    } else {
      this._console.warn('O gravador de mídia não está gravando ou pausado para parar.');
    }
  }

  /**
   * Reinicia o buffer de áudio e o status da gravação após a parada.
   * Não anula o MediaRecorder ou o MediaStream, permitindo futuras gravações.
   */
  private _handleMediaRecorderStop(): void {
    const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
    const audioBlob = new Blob(this.audioChunks, { type: mimeType });
    this.audioBlobSubject.next(audioBlob); // Emite o blob de áudio gravado
    this.audioChunks = []; // Limpa o array para a próxima gravação
    this.store.setRecordingStatus(RecordingStatus.INACTIVE); // Define o status como inativo
    this.stopSilenceDetection(); // Para a detecção de silêncio se estiver ativa
    this.stopAmplitudeAnalysis(); // Para a análise de amplitude
    this._console.log('Gravação finalizada e dados de áudio processados. Pronto para nova gravação.');
  }

  /**
   * Para todas as trilhas do MediaStream e anula o MediaRecorder.
   * Este método deve ser chamado apenas quando o serviço/componente for destruído.
   */
  stopDestroy(): void {
    this.stopSilenceDetection();
    if (this.mediaRecorder && (this.mediaRecorder.state === RecordingStatus.RECORDING || this.mediaRecorder.state === RecordingStatus.PAUSED)) {
      this.mediaRecorder.stop();
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    this.mediaRecorder = null;
    this.isMicrophoneReady.set(false);
    this.cleanupAudioContext(); // Limpia el AudioContext y el AnalyserNode
    this._console.log('Recursos do microfone destruídos.');
  }

  pauseRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === RecordingStatus.RECORDING) {
      this._uiStateService.stopRecording();
      this.mediaRecorder.pause();
      this.store.setRecordingStatus(RecordingStatus.PAUSED);
      this.store.isRecording.set(false);
      this.stopAmplitudeAnalysis();
    } else {
      this._console.warn('O gravador de mídia não está gravando para pausar.');
    }
  }

  resumeRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === RecordingStatus.PAUSED) {
      this._uiStateService.startRecording();
      this.mediaRecorder.resume();
      this.store.isRecording.set(true);
      this.store.setRecordingStatus(RecordingStatus.RECORDING);
      this.startAmplitudeAnalysis();
    } else {
      this._console.warn('O gravador de mídia não está pausado para retomar.');
    }
  }

  toggleSilenceDetection(enabled: boolean): void {
    this.silenceDetectionEnabled = enabled;
    this._console.log(`Detecção de silêncio ${enabled ? 'ativada' : 'desativada'}.`);
    if (enabled && this.mediaRecorder && this.mediaRecorder.state === RecordingStatus.RECORDING) {
      this.startSilenceDetection();
    } else {
      this.stopSilenceDetection();
    }
  }

  private startSilenceDetection(): void {
    if (!this.stream) {
      this._console.warn('Não há stream de áudio disponível para detecção de silêncio.');
      return;
    }

    if (!this.analyser) {
      this._console.warn('AnalyserNode não disponível para detecção de silêncio.');
      return;
    }

    this.analyser.fftSize = 512;
    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    let lastSoundTime = Date.now();
    this._console.log('Detecção de silêncio iniciada.');

    const detectSilence = () => {
      if (!this.mediaRecorder || this.mediaRecorder.state !== RecordingStatus.RECORDING) {
        this._console.log('Detecção de silêncio interrompida: gravador não está ativo.');
        this.stopSilenceDetection();
        return;
      }

      this.analyser?.getByteFrequencyData(dataArray); // Usar getByteFrequencyData para volume
      const amplitude = this.calculateAmplitude(dataArray);

      if (amplitude < this.silenceThreshold) {
        if (Date.now() - lastSoundTime > this.silenceDuration) {
          this._console.log('Silêncio detectado, parando gravação.');
          this.stopRecording();
          return;
        }
      } else {
        lastSoundTime = Date.now();
      }
      this.silenceTimeout = setTimeout(detectSilence, 100);
    };

    this.silenceTimeout = setTimeout(detectSilence, 100);
  }

  private stopSilenceDetection(): void {
    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
      this.silenceTimeout = null;
      this._console.log('Detecção de silêncio parada.');
    }
  }

  private startAmplitudeAnalysis(): void {
    if (!this.audioContext || !this.analyser) {
      this._console.warn('AudioContext ou Analyser não inicializados para análise de amplitude.');
      return;
    }

    // Garante que dataArray tenha o tamanho correto para getByteTimeDomainData (fftSize)
    if (!this.dataArray || this.dataArray.length !== this.analyser.fftSize) {
      this.dataArray = new Uint8Array(this.analyser.fftSize);
      this.bufferLength = this.analyser.fftSize; // Atualiza bufferLength para consistência
    }

    const analyze = () => {
      // Verifica o estado do mediaRecorder e se o audioContext ainda está rodando
      if (this.mediaRecorder && this.mediaRecorder.state === RecordingStatus.RECORDING && this.audioContext?.state === 'running') {
        this.analyser?.getByteTimeDomainData(this.dataArray!); // Obtém dados da forma de onda

        const amplitude = this.calculateAmplitude(this.dataArray!); // Amplitude média
        this.amplitudeSubject.next(amplitude);
        requestAnimationFrame(analyze);
      } else {
        this._console.log('Análise de amplitude parada.');
        this.amplitudeSubject.next(0); // Reinicia a amplitude
      }
    };
    this._console.log('Iniciando análise de amplitude...');
    requestAnimationFrame(analyze);
  }

  private stopAmplitudeAnalysis(): void {
    this.amplitudeSubject.next(0);
  }

  private cleanupAudioContext(): void {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
      this.analyser = null;
      this.dataArray = null;
      this.bufferLength = 0;
    }
  }

  private calculateAmplitude(audioData: Uint8Array): number {
    // 1. Detectar el pico máximo en el fragmento de audio
    let peak = 0;
    for (let i = 0; i < audioData.length; i++) {
      // Convertir el valor de byte (0-255) a una señal en el rango [-1, 1]
      const sample = (audioData[i] - 128) / 128;
      const absSample = Math.abs(sample);
      if (absSample > peak) peak = absSample;
    }

    // 2. Aplicar una amplificación no lineal (cúbica) para hacer más visibles los sonidos bajos
    const amplified = Math.pow(peak, 3) * 100;

    // 3. Escalar a un rango de 0-128 para que coincida con la visualización
    return Math.min(amplified * 128, 128);
  }
}
